import { memo, useEffect, useState } from "react";
import Toast from "../../../Components/ErrorBoundry/Toast";
import { NewModal } from "../../../Components/New/Modal/NewModal";
import { ModalId } from "../../../Components/New/Modal/ProfileModal/Const";
import { setModalHeaderFooter } from "../../../ReduxTool/Slice/CommonReducers/CommonReducers";
import { useAppDispatch, useAppSelector } from "../../../ReduxTool/store/hooks";
import { downloadInvoicePdf, getMyInvoices, getPaymentHistory, hasInvoiceId, HistoryPage, InvoiceRow } from "../../../lib/payments/billing";
import { paymentErrorMessage } from "../../../lib/payments/payu";

// Profile > Subscription & billing: every payment, a page at a time
// (GET /api/payments/history), and their invoices (GET /api/payments/invoices,
// the PDF from /invoices/{id}/download). Same screen as the EPC app's PaymentBilling.

const HISTORY_PAGE_SIZE = 10;

const formatDate = (value: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

const formatAmount = (amount: number | null, currency: string | null): string => {
  if (amount === null) return "—";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "INR", maximumFractionDigits: 2 }).format(amount);
  } catch {
    return `${currency ?? ""} ${amount.toFixed(2)}`.trim();
  }
};

const statusTone = (status: string | null): string => {
  const s = (status ?? "").toLowerCase();
  if (["success", "paid", "completed", "active"].includes(s)) return "bg-green-50 text-green-700";
  if (["failed", "cancelled", "canceled", "expired"].includes(s)) return "bg-red-50 text-red-700";
  if (["refunded"].includes(s)) return "bg-gray-100 text-gray-700";
  return "bg-amber-50 text-amber-700";
};

type Load<T> = { state: "loading" } | { state: "error" } | { state: "ready"; rows: T[] };

const PaymentBilling = () => {
  const [invoices, setInvoices] = useState<Load<InvoiceRow>>({ state: "loading" });
  const [history, setHistory] = useState<{ state: "loading" } | { state: "error" } | { state: "ready"; page: HistoryPage }>({ state: "loading" });
  const [historyPage, setHistoryPage] = useState(0);
  const [downloading, setDownloading] = useState<string | null>(null);

  const loadHistory = (page: number) => {
    setHistory({ state: "loading" });
    getPaymentHistory(page, HISTORY_PAGE_SIZE)
      .then((result) => setHistory({ state: "ready", page: result }))
      .catch(() => setHistory({ state: "error" }));
  };
  const load = () => {
    setInvoices({ state: "loading" });
    getMyInvoices().then((rows) => setInvoices({ state: "ready", rows })).catch(() => setInvoices({ state: "error" }));
    loadHistory(historyPage);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);
  const goToHistoryPage = (page: number) => {
    setHistoryPage(page);
    loadHistory(page);
  };

  const download = async (invoice: InvoiceRow) => {
    setDownloading(invoice.id);
    try {
      await downloadInvoicePdf(invoice);
    } catch (err) {
      Toast({ messageText: `Couldn't download the invoice: ${paymentErrorMessage(err)}`, messageType: "E" });
    } finally {
      setDownloading(null);
    }
  };

  const failed = (what: string) => (
    <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
      Couldn't load your {what}.{" "}
      <button type="button" onClick={load} className="font-semibold underline">Try again</button>
    </div>
  );
  const loading = <p className="text-xs text-gray-500">Loading…</p>;

  return (
    <div className="flex flex-col gap-6 text-left">
      {/* ── Payment history ── */}
      <section>
        <h3 className="mb-2 text-sm font-semibold text-gray-900">Payment history</h3>
        {history.state === "loading" && loading}
        {history.state === "error" && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            Couldn't load your payment history.{" "}
            <button type="button" onClick={() => loadHistory(historyPage)} className="font-semibold underline">Try again</button>
          </div>
        )}
        {history.state === "ready" && (history.page.rows.length ? (
          <>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-50 text-[11px] uppercase text-gray-500 whitespace-nowrap">
                  <tr>
                    <th className="px-3 py-2 font-medium">Date</th>
                    <th className="px-3 py-2 font-medium">Item</th>
                    <th className="px-3 py-2 font-medium">Plan</th>
                    <th className="px-3 py-2 text-right font-medium">Qty</th>
                    <th className="px-3 py-2 text-right font-medium">Amount</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Transaction</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {history.page.rows.map((row) => (
                    <tr key={row.id}>
                      <td className="whitespace-nowrap px-3 py-2 text-gray-700">{formatDate(row.date)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-gray-900">{row.item ?? "—"}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-gray-700">{row.basis ?? "—"}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{row.quantity ?? "—"}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-right text-gray-900">{formatAmount(row.amount, row.currency)}</td>
                      <td className="px-3 py-2">
                        {row.status
                          ? <span className={`inline-block whitespace-nowrap rounded px-2 py-0.5 text-[10px] font-semibold ${statusTone(row.status)}`}>{row.status}</span>
                          : "—"}
                      </td>
                      <td className="max-w-[9rem] truncate px-3 py-2 text-gray-500" title={`${row.id}${row.method ? ` · ${row.method}` : ""}`}>{row.id.startsWith("payment-") ? "—" : row.id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(historyPage > 0 || history.page.hasMore) && (
              <div className="mt-2 flex items-center justify-end gap-2 text-xs">
                <span className="text-gray-500">
                  Page {historyPage + 1}{history.page.total !== null ? ` of ${Math.max(1, Math.ceil(history.page.total / HISTORY_PAGE_SIZE))}` : ""}
                </span>
                <button type="button" disabled={historyPage === 0} onClick={() => goToHistoryPage(historyPage - 1)}
                  className="rounded border border-gray-300 px-2 py-1 text-gray-700 disabled:opacity-40">Previous</button>
                <button type="button" disabled={!history.page.hasMore} onClick={() => goToHistoryPage(historyPage + 1)}
                  className="rounded border border-gray-300 px-2 py-1 text-gray-700 disabled:opacity-40">Next</button>
              </div>
            )}
          </>
        ) : (
          <p className="rounded-md border border-dashed border-gray-300 px-3 py-4 text-center text-xs text-gray-500">
            {historyPage > 0 ? "No more payments." : "No payments yet. Every payment you make with PayU appears here."}
          </p>
        ))}
      </section>

      {/* ── Invoices ── */}
      <section>
        <h3 className="mb-2 text-sm font-semibold text-gray-900">Invoices</h3>
        {invoices.state === "loading" && loading}
        {invoices.state === "error" && failed("invoices")}
        {invoices.state === "ready" && (invoices.rows.length ? (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-left text-xs">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-500 whitespace-nowrap">
                <tr>
                  <th className="px-3 py-2 font-medium">Date</th>
                  <th className="px-3 py-2 font-medium">Invoice</th>
                  <th className="px-3 py-2 font-medium">Item</th>
                  <th className="px-3 py-2 text-right font-medium">Amount</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">PDF</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {invoices.rows.map((invoice) => (
                  <tr key={invoice.id}>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-700">{formatDate(invoice.date)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-900">{invoice.number ?? "—"}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-700">{invoice.description ?? "—"}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right text-gray-900">{formatAmount(invoice.amount, invoice.currency)}</td>
                    <td className="px-3 py-2">
                      {invoice.status
                        ? <span className={`inline-block whitespace-nowrap rounded px-2 py-0.5 text-[10px] font-semibold ${statusTone(invoice.status)}`}>{invoice.status}</span>
                        : "—"}
                    </td>
                    <td className="px-3 py-2">
                      {hasInvoiceId(invoice)
                        ? (
                          <button type="button" disabled={downloading === invoice.id} onClick={() => download(invoice)} 
                            className="whitespace-nowrap font-medium text-sky-700 underline disabled:opacity-50">
                            {downloading === invoice.id ? "Downloading…" : "Download"}
                          </button>
                        )
                        : <span className="text-gray-400" title="The invoice PDF is generated shortly after payment">Preparing</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="rounded-md border border-dashed border-gray-300 px-3 py-4 text-center text-xs text-gray-500">
            No invoices yet. Your invoices appear here after a successful payment.
          </p>
        ))}
      </section>
    </div>
  );
};

const PaymentBillingModal = () => {
  const dispatch = useAppDispatch();
  const { modalid } = useAppSelector((state) => state.commonReducers.modal);

  const closeModal = () => {
    dispatch(setModalHeaderFooter({ title: "", btnTxt: "", secondaryBtnTxt: "", isheader: false, modalid: "" }));
  };

  if (modalid !== ModalId.BillingPlans) return null;

  return (
    <NewModal modalSize="mdlg" maxheight="70vh" name="Subscription & Billing" btnName="" onClick={() => { }} setIsCLose={closeModal} isAbleCLick={false} isFooterRequired={false}>
      <PaymentBilling />
    </NewModal>
  );
};

export default memo(PaymentBillingModal);
